/**
 * Desktop Releases rollout controls (God Mode).
 *
 * The desktop_releases catalog is registered by the release train (rows land
 * inert at 0%); this panel is the shipping lever. The everyday operation is
 * "Ship to 100%" on a version, which activates every one of its target/arch
 * rows at full rollout in one click. Staged rollouts and deactivation are the
 * secondary paths. "Which version is the fleet offered" comes from the
 * backend (selectRelease semantics); the UI renders it, it never recomputes
 * release policy.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, MonitorDown, Power, Rocket } from "lucide-react";
import { Button } from "../../ui/button.js";

export interface DesktopReleaseRow {
  id: string;
  version: string;
  target: string;
  arch: string;
  url: string;
  signature: string;
  notes: string | null;
  pub_date: string;
  rollout_percent: number;
  active: boolean;
  created_at: string;
}

interface ReleasesResponse {
  releases: DesktopReleaseRow[];
  offeredVersion: string | null;
}

interface VersionGroup {
  version: string;
  rows: DesktopReleaseRow[];
}

const ROLLOUT_PRESETS = [10, 25, 50, 100] as const;

async function responseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : `Request failed (${res.status})`;
}

export function DesktopReleasesPanel() {
  const [releases, setReleases] = useState<DesktopReleaseRow[]>([]);
  const [offeredVersion, setOfferedVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyVersion, setBusyVersion] = useState<string | null>(null);
  const [percentDrafts, setPercentDrafts] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/desktop-releases");
      if (!res.ok) {
        setLoadError(await responseError(res));
        return;
      }
      const body = (await res.json()) as ReleasesResponse;
      setReleases(body.releases);
      setOfferedVersion(body.offeredVersion);
      setLoadError(null);
    } catch {
      setLoadError("Unable to load the desktop release catalog.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Group by version, preserving the server's created_at DESC order so the
  // newest registration leads.
  const groups = useMemo<VersionGroup[]>(() => {
    const byVersion = new Map<string, DesktopReleaseRow[]>();
    for (const row of releases) {
      const existing = byVersion.get(row.version);
      if (existing) existing.push(row);
      else byVersion.set(row.version, [row]);
    }
    return Array.from(byVersion, ([version, rows]) => ({ version, rows }));
  }, [releases]);

  async function patchVersion(
    group: VersionGroup,
    body: { active?: boolean; rolloutPercent?: number },
  ) {
    setBusyVersion(group.version);
    try {
      const results = await Promise.all(
        group.rows.map((row) =>
          fetch(`/api/admin/desktop-releases/${encodeURIComponent(row.id)}`, {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          }),
        ),
      );
      const failed = results.filter((res) => !res.ok);
      if (failed.length > 0) {
        alert(
          `${failed.length} of ${group.rows.length} rows for ${group.version} failed to update: ` +
            `${await responseError(failed[0])}. Reloading the current state.`,
        );
      }
      await refresh();
    } finally {
      setBusyVersion(null);
    }
  }

  function shipVersion(group: VersionGroup) {
    void patchVersion(group, { active: true, rolloutPercent: 100 });
  }

  function setRollout(group: VersionGroup, percent: number) {
    if (!Number.isInteger(percent) || percent < 0 || percent > 100) {
      alert("Rollout percent must be a whole number from 0 to 100.");
      return;
    }
    void patchVersion(group, { active: true, rolloutPercent: percent });
  }

  function deactivateVersion(group: VersionGroup) {
    const confirmed = window.confirm(
      `Stop offering ${group.version} to the fleet?\n\n` +
        "Installs that already updated keep this version. Deactivating only stops offering it; it is not a rollback.",
    );
    if (!confirmed) return;
    void patchVersion(group, { active: false });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-sm" role="alert" style={{ color: "var(--error)" }}>
        {loadError}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Fleet headline */}
      <div
        className="rounded-[var(--radius-card)] border px-4 py-3 flex items-center gap-3"
        style={{
          borderColor: "var(--print-border)",
          background: "var(--print-bg)",
          boxShadow: "var(--shadow-print)",
        }}
      >
        <MonitorDown size={18} style={{ color: "var(--accent)" }} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
            {offeredVersion
              ? `The fleet is currently offered ${offeredVersion}`
              : "No active release. The fleet is not being offered an update."}
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Newest active version wins. Rollout changes take effect on the next updater poll.
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-sm py-4" style={{ color: "var(--text-secondary)" }}>
          No desktop releases registered yet. The release train registers rows here inert at 0%.
        </div>
      ) : (
        groups.map((group) => (
          <VersionCard
            key={group.version}
            group={group}
            offered={group.version === offeredVersion}
            busy={busyVersion === group.version}
            anyBusy={busyVersion !== null}
            percentDraft={percentDrafts[group.version] ?? ""}
            onPercentDraftChange={(value) =>
              setPercentDrafts((prev) => ({ ...prev, [group.version]: value }))
            }
            onShip={() => shipVersion(group)}
            onSetRollout={(pct) => setRollout(group, pct)}
            onDeactivate={() => deactivateVersion(group)}
          />
        ))
      )}
    </div>
  );
}

function VersionCard({
  group,
  offered,
  busy,
  anyBusy,
  percentDraft,
  onPercentDraftChange,
  onShip,
  onSetRollout,
  onDeactivate,
}: {
  group: VersionGroup;
  offered: boolean;
  busy: boolean;
  anyBusy: boolean;
  percentDraft: string;
  onPercentDraftChange: (value: string) => void;
  onShip: () => void;
  onSetRollout: (percent: number) => void;
  onDeactivate: () => void;
}) {
  const { version, rows } = group;
  const fullyLive = rows.every((row) => row.active && row.rollout_percent === 100);
  const anyActive = rows.some((row) => row.active);
  const pubDate = rows.reduce<string | null>(
    (latest, row) => (latest === null || row.pub_date > latest ? row.pub_date : latest),
    null,
  );
  const notes = rows.find((row) => row.notes)?.notes ?? null;

  function applyDraft() {
    const trimmed = percentDraft.trim();
    if (trimmed === "") return;
    onSetRollout(Number(trimmed));
  }

  return (
    <div
      className="rounded-[var(--radius-card)] border"
      style={{
        borderColor: offered ? "var(--accent)" : "var(--print-border)",
        background: "var(--print-bg)",
        boxShadow: "var(--shadow-print)",
      }}
    >
      {/* Header */}
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <span
            className="text-sm font-semibold font-mono"
            style={{ color: "var(--text-primary)" }}
          >
            {version}
          </span>
          {offered && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
            >
              Offered to fleet
            </span>
          )}
          {!anyActive && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{
                background: "rgba(var(--ink-rgb), 0.06)",
                color: "var(--text-tertiary)",
              }}
            >
              Inactive
            </span>
          )}
        </div>
        {pubDate && (
          <span className="text-xs" style={{ color: "var(--text-tertiary)" }}>
            Published {new Date(pubDate).toLocaleDateString()}
          </span>
        )}
      </div>

      {notes && (
        <div
          className="px-4 py-2 text-xs"
          style={{ color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)" }}
        >
          {notes}
        </div>
      )}

      {/* Per target/arch rows */}
      <div className="px-4 py-2" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
        <table className="w-full text-[12px]" style={{ color: "var(--text-primary)" }}>
          <thead>
            <tr>
              {["Target", "Arch", "Active", "Rollout"].map((label) => (
                <th
                  key={label}
                  className="text-left py-1 pr-3 text-[10px] uppercase tracking-wider font-medium"
                  style={{ color: "var(--text-label)", fontFamily: "var(--font-mono)" }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="py-1 pr-3 font-mono">{row.target}</td>
                <td className="py-1 pr-3 font-mono">{row.arch}</td>
                <td className="py-1 pr-3">
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-[1px] rounded"
                    style={{
                      background: row.active
                        ? "color-mix(in srgb, var(--success) 15%, transparent)"
                        : "rgba(var(--ink-rgb), 0.06)",
                      color: row.active ? "var(--success)" : "var(--text-tertiary)",
                    }}
                  >
                    {row.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="py-1 pr-3 font-mono">{row.rollout_percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <Button
          type="button"
          size="sm"
          onClick={onShip}
          disabled={anyBusy || fullyLive}
          aria-label={`Ship ${version} to 100%`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />}
          Ship to 100%
        </Button>

        <div className="flex items-center gap-1 ml-2">
          <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            Rollout:
          </span>
          {ROLLOUT_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => onSetRollout(preset)}
              disabled={anyBusy}
              className="px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50"
              style={{ background: "rgba(var(--ink-rgb), 0.06)", color: "var(--text-secondary)" }}
            >
              {preset}%
            </button>
          ))}
          <input
            type="number"
            min={0}
            max={100}
            value={percentDraft}
            onChange={(e) => onPercentDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyDraft();
            }}
            disabled={anyBusy}
            aria-label={`Custom rollout percent for ${version}`}
            placeholder="%"
            className="w-14 rounded-md border px-2 py-1 text-[11px] disabled:opacity-50"
            style={{
              borderColor: "var(--border)",
              background: "var(--bg-input)",
              color: "var(--text-primary)",
            }}
          />
          <button
            type="button"
            onClick={applyDraft}
            disabled={anyBusy || percentDraft.trim() === ""}
            className="px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50"
            style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
          >
            Set
          </button>
        </div>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDeactivate}
          disabled={anyBusy || !anyActive}
          className="ml-auto text-[var(--error)] hover:bg-[var(--error-muted)]"
        >
          <Power size={12} />
          Deactivate
        </Button>
      </div>

      <div
        className="px-4 pb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        Deactivate stops offering this version; installs that already updated keep it. This is not a rollback.
      </div>
    </div>
  );
}
